import { theme } from '@Guidelines/Theme/theme';
import { css } from '@emotion/react';
import { CircleAlert, TriangleAlert } from 'lucide-react';
import * as React from 'react';
import { Button, ConfirmDialog, Dialog } from '@verclam/ui-kit';

const bodyStyle = css`
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 0.75rem;
`;

const textStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.medium};
	line-height: 1.6;
	color: ${theme.colors.inkSecondary};
`;

type Props = {
	readonly visible: boolean;
	/** `blocked`: invalid fields forbid the validation; `warning`: fields remain to be verified. */
	readonly kind: 'blocked' | 'warning';
	readonly invalidLabels: readonly string[];
	readonly toVerifyCount: number;
	readonly unverifiedCount: number;
	readonly isValidating: boolean;
	readonly onGoToField: () => void;
	readonly onConfirm: () => void;
	readonly onClose: () => void;
};

const formatFieldCount = (count: number, singular: string, plural: string): string =>
	count === 1 ? `1 champ ${singular}` : `${count} champs ${plural}`;

/** The two dialogs of « Valider le document »: the blocking one (invalid fields) and the warning one. */
const ValidationDialog: React.FC<Props> = ({
	visible,
	kind,
	invalidLabels,
	toVerifyCount,
	unverifiedCount,
	isValidating,
	onGoToField,
	onConfirm,
	onClose,
}) => {
	const blockedFooter = React.useMemo(
		() => <Button type="button" label="Fermer" severity="secondary" outlined onClick={onClose} />,
		[onClose],
	);

	if (kind === 'blocked') {
		return (
			<Dialog
				visible={visible}
				onHide={onClose}
				header="Le document ne peut pas être validé"
				footer={blockedFooter}
				draggable={false}
				resizable={false}
				style={{ width: '32rem', maxWidth: '95vw' }}
			>
				<div css={bodyStyle}>
					<p css={textStyle}>
						<CircleAlert size={16} aria-hidden="true" /> Le document serait faux à l’impression. À
						corriger : {invalidLabels.join(', ')}.
					</p>
					<Button
						type="button"
						text
						label="Aller au premier champ invalide"
						onClick={onGoToField}
					/>
				</div>
			</Dialog>
		);
	}

	const unverifiedPart =
		unverifiedCount > 0 ? ` et ${formatFieldCount(unverifiedCount, 'non vérifié', 'non vérifiés')}` : '';

	return (
		<ConfirmDialog
			visible={visible}
			onHide={onClose}
			header="Valider le document ?"
			icon={<TriangleAlert size={24} aria-hidden="true" />}
			message={
				<div css={bodyStyle}>
					<p css={textStyle}>
						{formatFieldCount(toVerifyCount, 'à vérifier', 'à vérifier')}
						{unverifiedPart}. Vous pourrez encore les vérifier ou les corriger après la validation
						: toute modification rouvrira la vérification.
					</p>
					<Button
						type="button"
						text
						label="Aller au premier champ à vérifier"
						onClick={onGoToField}
					/>
				</div>
			}
			acceptLabel="Valider quand même"
			rejectLabel="Annuler"
			accept={onConfirm}
			reject={onClose}
			loading={isValidating}
		/>
	);
};

export default ValidationDialog;
